import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Plus, Search, UserRound, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { routeSettersService } from '../../services/route-setters';
import type {
  RouteSetter,
  RouteSetterCandidate,
  RouteSetterSearchResults,
} from '../../types/route-setter';

/** The shape call sites hold on to. Kept as an alias so they read as before. */
export type RoutesetterSelection = RouteSetter;

interface RoutesetterPickerProps {
  mode: 'single' | 'multiple';
  value: RouteSetter[];
  onChange: (next: RouteSetter[]) => void;
  /** The location's slug. Its roster is what opens before anybody types. */
  locationId?: string | null;
  placeholder?: string;
  /**
   * The reader's user id, when they have no climb role at the place — a climber
   * crediting a climb they put up. They pick from the setters that exist or
   * credit themselves; adding anybody to the roster is the place's to do, and
   * the server refuses it (see Api::Locations::RouteSettersController#member_create).
   */
  memberUserId?: string | null;
}

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_RESULTS: RouteSetterSearchResults = { route_setters: [], users: [] };

function initial(text: string | null | undefined): string {
  return (text ?? '').trim().charAt(0).toUpperCase() || '?';
}

export function RoutesetterPicker({
  mode,
  value,
  onChange,
  locationId,
  placeholder,
  memberUserId = null,
}: RoutesetterPickerProps) {
  const { t } = useTranslation();
  const member = !!memberUserId;
  // A name that says what the box is, and matches no field the browser knows
  // how to fill — one more thing its heuristics read before deciding what this
  // is. See the input below for the rest of that argument.
  const searchFieldName = `routesetter-search-${useId()}`;
  const [roster, setRoster] = useState<RouteSetter[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<RouteSetterSearchResults>(EMPTY_RESULTS);
  const [searchLoading, setSearchLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSelected = useCallback(
    (id: string) => value.some((s) => s.id === id),
    [value]
  );

  const loadRoster = useCallback(() => {
    if (!locationId) {
      setRoster([]);
      return;
    }
    setRosterLoading(true);
    routeSettersService
      .roster(locationId)
      .then(setRoster)
      .catch(() => setRoster([]))
      .finally(() => setRosterLoading(false));
  }, [locationId]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  function runSearch(query: string) {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim() || !locationId) {
      setResults(EMPTY_RESULTS);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(() => {
      routeSettersService
        .search(locationId, query)
        .then(setResults)
        .catch(() => setResults(EMPTY_RESULTS))
        .finally(() => setSearchLoading(false));
    }, SEARCH_DEBOUNCE_MS);
  }

  function toggleSelection(setter: RouteSetter) {
    if (mode === 'single') {
      onChange(isSelected(setter.id) ? [] : [setter]);
    } else if (isSelected(setter.id)) {
      onChange(value.filter((s) => s.id !== setter.id));
    } else {
      onChange([...value, setter]);
    }
  }

  function removeSelection(id: string) {
    onChange(value.filter((s) => s.id !== id));
  }

  function clearSearch() {
    setSearchTerm('');
    setResults(EMPTY_RESULTS);
    setError(null);
  }

  /**
   * Turns something that is not a setter yet into one, then selects it. The
   * record has to exist before it can be assigned, so every write elsewhere
   * still sends a single id and knows nothing about any of this.
   */
  async function materialise(attributes: { user_id: string } | { name: string }) {
    if (!locationId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await routeSettersService.create(locationId, attributes);
      if (mode === 'single') {
        onChange([created]);
      } else if (!isSelected(created.id)) {
        onChange([...value, created]);
      }
      clearSearch();
      loadRoster();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('routesetterPicker.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  const query = searchTerm.trim();
  const isSearching = query.length > 0;
  const hasResults = results.route_setters.length > 0 || results.users.length > 0;
  // Only offer to create when nothing already goes by that name — otherwise the
  // button invites a duplicate of the row right above it.
  const exactMatch = results.route_setters.some(
    (s) => s.name.trim().toLowerCase() === query.toLowerCase()
  );
  const canCreateGuest = !member && isSearching && !searchLoading && !exactMatch;
  // The reader's own setter record, when it is the one picked: an account
  // has one record at most, so the user id is enough to tell.
  const selfSelected = !!memberUserId && value.some((s) => s.user_id === memberUserId);

  function renderRow(setter: RouteSetter | RouteSetterCandidate, onPick: () => void) {
    const selected = setter.id !== null && isSelected(setter.id);
    return (
      <li key={setter.id ?? `user-${setter.user_id}`}>
        <button
          type="button"
          onClick={onPick}
          disabled={busy}
          className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors disabled:opacity-50 ${
            selected ? 'bg-primary/5' : 'hover:bg-muted'
          }`}
          aria-pressed={selected}
        >
          <Avatar className="h-9 w-9 shrink-0">
            {setter.avatar_small_url && <AvatarImage src={setter.avatar_small_url} alt="" />}
            <AvatarFallback>{initial(setter.name)}</AvatarFallback>
          </Avatar>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium truncate">{setter.name}</span>
            {setter.username && (
              <span className="block text-xs text-muted-foreground truncate">
                @{setter.username}
              </span>
            )}
          </span>
          {selected && <Check className="w-4 h-4 text-primary shrink-0" />}
        </button>
      </li>
    );
  }

  return (
    <div className="space-y-3" data-testid="routesetter-picker">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="routesetter-selected-list">
          {value.map((setter) => (
            <span
              key={setter.id}
              className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full bg-muted border border-border text-sm"
            >
              <Avatar className="h-6 w-6">
                {setter.avatar_small_url && <AvatarImage src={setter.avatar_small_url} alt="" />}
                <AvatarFallback className="text-xs">{initial(setter.name)}</AvatarFallback>
              </Avatar>
              <span className="font-medium">{setter.name}</span>
              <button
                type="button"
                onClick={() => removeSelection(setter.id)}
                className="text-muted-foreground hover:text-destructive"
                aria-label={t('routesetterPicker.remove', { name: setter.name })}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        {/* A search box, and said so with `type="search"` rather than left to be
            guessed at: "nome" in the placeholder is all Chrome needs to read a
            text field as a name and drop the addresses it has saved over the
            results — and `autocomplete="off"` alone does not stop that, since
            Chrome honours it only for its own history of what was typed. Its
            name and address heuristics do not look at search fields at all,
            and every browser takes a search box for one. The `off` stays for
            that typed history, which Chrome does keep on search boxes. The
            WebKit clear button is hidden: the list below has its own. */}
        <Input
          type="search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            runSearch(e.target.value);
          }}
          placeholder={placeholder ?? t('routesetterPicker.searchPlaceholder')}
          className="pl-9 [&::-webkit-search-cancel-button]:appearance-none"
          aria-label={t('routesetterPicker.searchPlaceholder')}
          name={searchFieldName}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-form-type="other"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
        />
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {/* A climber's way to say "I set it" — which they usually did. Their
          setter record may not exist yet, so this goes through the same
          materialise as an account picked from the search. */}
      {memberUserId && !selfSelected && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full text-xs"
          disabled={busy}
          onClick={() => materialise({ user_id: memberUserId })}
          data-testid="routesetter-pick-self"
        >
          <UserRound className="w-3.5 h-3.5 mr-1" />
          {t('routesetterPicker.pickSelf')}
        </Button>
      )}

      {/* At rest: who this place has said sets here. */}
      {!isSearching && locationId && (
        <div data-testid="routesetter-gym-grid">
          {rosterLoading && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
          {!rosterLoading && roster.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t(member ? 'routesetterPicker.noGymRoutesettersMember' : 'routesetterPicker.noGymRoutesetters')}
            </p>
          )}
          {!rosterLoading && roster.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {roster.map((setter) => {
                const selected = isSelected(setter.id);
                return (
                  <button
                    type="button"
                    key={setter.id}
                    onClick={() => toggleSelection(setter)}
                    className={`flex items-center gap-1.5 px-1.5 py-1 border rounded-md transition-colors text-left ${
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                    }`}
                    aria-pressed={selected}
                  >
                    <Avatar className="h-6 w-6 shrink-0">
                      {setter.avatar_small_url && (
                        <AvatarImage src={setter.avatar_small_url} alt="" />
                      )}
                      <AvatarFallback className="text-[10px]">{initial(setter.name)}</AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-medium leading-tight truncate flex-1 min-w-0">
                      {setter.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isSearching && (
        <div data-testid="routesetter-search-results" className="space-y-2">
          {searchLoading && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}

          {!searchLoading && results.route_setters.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">
                {t('routesetterPicker.groupSetters')}
              </p>
              <ul className="border border-border rounded-md divide-y divide-border overflow-hidden">
                {results.route_setters.map((setter) =>
                  renderRow(setter, () => toggleSelection(setter))
                )}
              </ul>
            </div>
          )}

          {/* Accounts nobody has credited yet: picking one makes it a setter. */}
          {!searchLoading && results.users.length > 0 && (
            <div data-testid="routesetter-user-results">
              <p className="text-xs font-medium text-muted-foreground mb-1">
                {t('routesetterPicker.groupUsers')}
              </p>
              <ul className="border border-border rounded-md divide-y divide-border overflow-hidden">
                {results.users.map((candidate) =>
                  renderRow(candidate, () => materialise({ user_id: candidate.user_id }))
                )}
              </ul>
            </div>
          )}

          {!searchLoading && !hasResults && (
            <p className="text-xs text-muted-foreground">{t('routesetterPicker.noSearchResults')}</p>
          )}

          {canCreateGuest && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full text-xs"
              disabled={busy}
              onClick={() => materialise({ name: query })}
              data-testid="routesetter-add-guest"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              {t('routesetterPicker.addGuest', { name: query })}
            </Button>
          )}

          <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={clearSearch}>
            {t('routesetterPicker.clearSearch')}
          </Button>
        </div>
      )}
    </div>
  );
}
